import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { track, Events } from '../utils/analytics';
import { supabase } from '../lib/supabaseClient';
import toast from 'react-hot-toast';
import {
  Inbox,
  User,
  Users,
  Zap,
  MessageSquare,
  X,
  Settings,
  LogOut,
  Shield,
  UserPlus,
} from 'lucide-react';
import VybeIcon from './icons/VybeIcon';
import InviteModal from './InviteModal';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  setActivePage: (page: string) => void;
  activePage: string;
}

/**
 * Render either a Lucide icon or our custom SVG without
 * overriding the VybeIcon's built-in gradient stroke.
 */
function IconRenderer({
  Icon,
  isActive,
}: {
  Icon: React.ElementType;
  isActive: boolean;
}) {
  // Use size so Lucide + VybeIcon behave the same
  // Avoid stroke/fill classes so we don't clobber VybeIcon's gradient
  return <Icon size={20} className="w-5 h-5 flex-shrink-0" />;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, setActivePage, activePage }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isAuthenticated,
    currentUser,
    logout,
    isVerified,
  } = useAuth();

  const [pendingAppCount, setPendingAppCount] = useState(0);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [showInviteModal, setShowInviteModal] = useState(false);

  const goPremium = () => {
    track(Events.PremiumCTA, { from: 'sidebar' });
    navigate('/pricing');
    onClose();
  };

  const manageBilling = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('create-portal-session');
      if (error) throw error;
      if (data?.url) {
        window.location.href = data.url;
      } else {
        navigate('/myAccount');
      }
    } catch (error) {
      console.error('Error opening billing portal:', error);
      toast.error('Unable to open billing portal. Please try again.');
      navigate('/myAccount');
    }
    onClose();
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setActivePage(path.replace('/', ''));
    onClose();
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      onClose();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Load pending application count
  useEffect(() => {
    const loadPendingCount = async () => {
      if (!currentUser?.id) {
        setPendingAppCount(0);
        return;
      }

      try {
        const { data: events, error: eventsError } = await supabase
          .from('events')
          .select('id')
          .eq('poster_id', currentUser.id);

        if (eventsError || !events || events.length === 0) {
          setPendingAppCount(0);
          return;
        }

        const eventIds = events.map(e => e.id);

        const { count, error: countError } = await supabase
          .from('event_applications')
          .select('*', { count: 'exact', head: true })
          .in('event_id', eventIds)
          .eq('status', 'pending');

        if (!countError && count !== null) {
          setPendingAppCount(count);
        }
      } catch (error) {
        console.error('Error loading pending application count:', error);
      }
    };

    loadPendingCount();

    if (!currentUser?.id) return;

    const channel = supabase
      .channel('pending-apps-' + currentUser.id)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'event_applications',
          filter: `host_id=eq.${currentUser.id}`,
        },
        () => {
          loadPendingCount();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentUser?.id]);

  // Load unread messages count
  useEffect(() => {
    const loadUnreadMessagesCount = async () => {
      if (!currentUser?.id) {
        setUnreadMessagesCount(0);
        return;
      }

      try {
        const { count, error } = await supabase
          .from('messages')
          .select('*', { count: 'exact', head: true })
          .eq('receiver_id', currentUser.id)
          .eq('read', false);

        if (!error && count !== null) {
          setUnreadMessagesCount(count);
        }
      } catch (error) {
        console.error('Error loading unread messages count:', error);
      }
    };

    loadUnreadMessagesCount();

    if (!currentUser?.id) return;

    const channel = supabase
      .channel('unread-messages-' + currentUser.id)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
          filter: `receiver_id=eq.${currentUser.id}`,
        },
        () => {
          loadUnreadMessagesCount();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentUser?.id]);

  const isAdmin =
    currentUser?.email === 'admin@example.com' ||
    currentUser?.email === 'admin@companionfinder.co.uk' ||
    currentUser?.email === 'admin@vybetribe.app';
  const isSubscribed = currentUser?.subscriptionTier === 'subscribed';

  const NavItem = ({
    path,
    icon,
    label,
    badge
  }: {
    path: string;
    icon: React.ElementType; // supports Lucide or custom SVG component
    label: string;
    badge?: number;
  }) => {
    const isActive = location.pathname === path || location.pathname.startsWith(path + '/');

    return (
      <li className="relative">
        <button
          onClick={() => handleNavigation(path)}
          className={`w-full flex items-center justify-start gap-3 px-3 py-2.5 font-medium transition-all duration-200 ease-in-out min-h-[44px] group
            ${
              isActive
                ? 'text-white shadow-lg bg-gradient-to-r from-red-500 to-pink-600 rounded-full'
                : 'text-white hover:bg-theme-cards hover:text-white rounded-lg'
            }`}
        >
          <IconRenderer Icon={icon} isActive={isActive} />
          <span className="text-left whitespace-nowrap" title={label}>
            {label}
          </span>
          {badge !== undefined && badge > 0 && (
            <span className="ml-auto bg-gradient-to-r from-red-500 to-pink-600 text-white text-xs font-bold px-2 py-1 rounded-full min-w-[24px] text-center flex-shrink-0">
              {badge}
            </span>
          )}
        </button>
      </li>
    );
  };

  return (
    <div className="h-full flex flex-col bg-theme-background shadow-card border-r border-theme-cards">
      <div className="p-4 border-b border-theme-cards flex justify-end items-center lg:hidden">
        <button
          onClick={onClose}
          className="p-2 rounded-full hover:bg-theme-cards text-theme-text-main min-h-[44px] min-w-[44px] flex items-center justify-center"
          aria-label="Close menu"
        >
          <X size={20} />
        </button>
      </div>

      <nav className="flex-1 p-3 sm:p-4 overflow-y-auto">
        <ul className="space-y-2">
          {/* Explore Vybes uses the transparent circled-V icon */}
          <NavItem
            path="/vybes"
            icon={VybeIcon}
            label="Explore Vybes"
          />

          {isAuthenticated ? (
            <>
              <NavItem path="/tribe" icon={Users} label="Tribe" />
              <NavItem path="/messages" icon={MessageSquare} label="Messages" badge={unreadMessagesCount} />
              <NavItem path="/myvybes" icon={Zap} label="My Vybes" badge={pendingAppCount} />
              <NavItem path="/myvyberequests" icon={Inbox} label="My Vybe Requests" />
              <NavItem path="/myAccount" icon={User} label="My Profile" />

              {!isVerified() && <NavItem path="/verify" icon={Shield} label="Verify Account" />}

              {/* Invite Friends - Desktop Only */}
              <li className="relative hidden lg:block">
                <button
                  onClick={() => {
                    setShowInviteModal(true);
                    onClose();
                  }}
                  className="w-full flex items-center justify-start gap-3 px-3 py-2.5 font-medium transition-all duration-200 ease-in-out min-h-[44px] group text-white hover:bg-theme-cards hover:text-white rounded-lg"
                >
                  <UserPlus size={20} className="w-5 h-5 flex-shrink-0" />
                  <span className="text-left whitespace-nowrap">Invite Friends</span>
                </button>
              </li>

              <InviteModal
                isOpen={showInviteModal}
                onClose={() => setShowInviteModal(false)}
              />

              {isAdmin && <NavItem path="/admin" icon={Settings} label="Admin" />}

              <li className="mt-6 pt-6 border-t border-theme-cards">
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to log out?')) {
                      handleLogout();
                    }
                  }}
                  className="w-full flex items-center justify-start gap-3 px-3 py-2.5 rounded-lg transition-all min-h-[44px]"
                  style={{ color: '#ff3148' }}
                >
                  <LogOut size={20} className="flex-shrink-0" />
                  <span className="text-left whitespace-nowrap">Logout</span>
                </button>
              </li>
            </>
          ) : (
            <>
              {/* Auth CTAs for logged-out users - Mobile Only */}
              <li className="mt-4 mb-2 md:hidden">
                <button
                  onClick={() => {
                    navigate('/login?signup=true');
                    onClose();
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white rounded-full font-semibold transition-all shadow-lg min-h-[44px]"
                >
                  <UserPlus size={20} />
                  <span>Join Free</span>
                </button>
              </li>

              <li className="md:hidden">
                <button
                  onClick={() => {
                    navigate('/login');
                    onClose();
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-theme-cards hover:bg-theme-background text-white rounded-lg font-medium transition-all border border-white/10 min-h-[44px]"
                >
                  <User size={20} />
                  <span>Login</span>
                </button>
              </li>
            </>
          )}
        </ul>
      </nav>
    </div>
  );
};

export default Sidebar;
